import * as Sentry from '@sentry/react';
import { getAnalytics } from 'firebase/analytics';
import { initializeApp } from 'firebase/app';
import {
  browserLocalPersistence,
  browserPopupRedirectResolver,
  signOut as firebaseSignOut,
  initializeAuth,
  onAuthStateChanged,
  type User as FirebaseUser,
} from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';

import { env } from '@/config/env';
import { isDev, isLocalDev, isStaging } from '@/config/environment';

const firebaseConfig = {
  apiKey: env.FIREBASE_API_KEY,
  authDomain: env.FIREBASE_AUTH_DOMAIN,
  projectId: env.FIREBASE_PROJECT_ID,
  storageBucket: env.FIREBASE_STORAGE_BUCKET,
  messagingSenderId: env.FIREBASE_MESSAGING_SENDER_ID,
  appId: env.FIREBASE_APP_ID,
  measurementId: env.FIREBASE_MEASUREMENT_ID,
};

const firebaseApp = initializeApp(firebaseConfig);

export const firebaseAnalytics = getAnalytics(firebaseApp);

export const firebaseAuth = initializeAuth(firebaseApp, {
  persistence: browserLocalPersistence,
  popupRedirectResolver: browserPopupRedirectResolver,
});
export const firebaseDb = getFirestore(firebaseApp);
export const firebaseStorage = getStorage(firebaseApp);

firebaseAuth.useDeviceLanguage();

const getCookieConfig = () => {
  const baseDomain = '.bey.chat';
  if (isLocalDev) {
    return { name: '__session_local', domain: 'localhost' };
  }
  if (isDev) {
    return { name: '__session_dev', domain: baseDomain };
  }
  if (isStaging) {
    return { name: '__session_staging', domain: baseDomain };
  }
  return { name: '__session', domain: baseDomain };
};

const setSessionCookie = (token: string | null) => {
  const { name, domain } = getCookieConfig();
  if (token) {
    document.cookie = `${name}=${token}; domain=${domain}; path=/; secure; samesite=Strict; max-age=604800`;
  } else {
    document.cookie = `${name}=; domain=${domain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; secure; samesite=Strict`;
  }
};

export const initializeAuthObserver = (
  callback?: (user: FirebaseUser | null) => void,
) => {
  return onAuthStateChanged(firebaseAuth, async (user) => {
    if (user) {
      try {
        const token = await user.getIdToken();
        setSessionCookie(token);
      } catch (error) {
        Sentry.captureException(error);
      }
    } else {
      setSessionCookie(null);
    }
    callback?.(user);
  });
};

export const signOut = async (): Promise<{
  success: boolean;
  error?: string;
}> => {
  try {
    setSessionCookie(null);
    await firebaseSignOut(firebaseAuth);
    return { success: true };
  } catch (error) {
    Sentry.captureException(error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

export type { FirebaseUser };
